import React, { useCallback } from "react";
import {
  CheckCircleIcon,
  CloudArrowDownIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/16/solid";
import { Button } from "component-library";
import { useTranslation } from "react-i18next";
import type { Report } from "./hooks/useReports";
import { formatDate, getReportDisplayName, isReportPending } from "./utils";

interface ReportCardProps {
  report: Report;
  onDownload: (reportId: string) => void;
}

export const ReportCard = React.memo(
  React.forwardRef<HTMLDivElement, ReportCardProps>(
    ({ report, onDownload }, ref) => {
      const { t } = useTranslation();

      const handleDownloadClick = useCallback(() => {
        onDownload(report.id);
      }, [onDownload, report.id]);

      const isPending = isReportPending(report);
      const displayName = getReportDisplayName(report);

      return (
        <div
          ref={ref}
          className="bg-white border-gray-200 flex flex-col md:flex-row md:items-center gap-3 p-3 md:p-4 border rounded-lg transition-all duration-200"
        >
          <div className="flex-1 space-y-2">
            <div className="flex items-start md:items-center gap-2 flex-row">
              <span className="break-words font-medium text-gray-900">
                {displayName}
              </span>
              {isPending && (
                <span className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-medium hidden lg:block">
                  {t("dashboard.pending")}
                </span>
              )}
              {isPending ? (
                <ExclamationCircleIcon
                  className="h-5 w-5 text-orange-500 flex-shrink-0 lg:hidden"
                  aria-label={t("dashboard.pendingAriaLabel")}
                />
              ) : (
                <CheckCircleIcon
                  className="h-4 w-4 text-green-600 flex-shrink-0"
                  aria-label={t("dashboard.downloadedAriaLabel")}
                />
              )}
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-4 text-sm text-gray-600">
              <span>
                {t("dashboard.created")}: {formatDate(report.createdAt)}
              </span>
              {report.downloadedAt && (
                <>
                  <span className="hidden md:inline text-gray-400">•</span>
                  <span>
                    {t("dashboard.lastDownloaded")}:{" "}
                    {formatDate(report.downloadedAt)}
                  </span>
                </>
              )}
            </div>
          </div>

          <Button
            variant={isPending ? "contained" : "outlined"}
            onClick={handleDownloadClick}
            className={`
          self-end md:self-center min-w-fit
          ${isPending ? "bg-primary hover:bg-primaryHover" : ""}
        `}
            aria-label={t("dashboard.downloadReport", {
              reportName: displayName,
            })}
          >
            <CloudArrowDownIcon className="h-4 w-4" />
          </Button>
        </div>
      );
    },
  ),
);

ReportCard.displayName = "ReportCard";
